// @ts-nocheck — composed component; types added when touched again later
import React from "react";
import { listUsers } from "../../lib/data/users";
import { useViewer } from "../../lib/data/viewer";
// Phase 3b — adopt v2 primitives. UserPicker stays a route-level takeover
// (NOT a Modal — it renders BEFORE the dashboard, with no content behind
// to veil). Audit §3.3 originally proposed Modal absorption; on closer look,
// the picker's semantics are "full-screen entry view", not "transient
// overlay", so we keep it as its own composed component but consume Card +
// Button primitives.
import Card from "../ui/Card";
import Button from "../ui/Button";
// Phase 3e — section-level empty/error/loading states use primitives.
import EmptyState from "../ui/EmptyState";
import ErrorState from "../ui/ErrorState";
import Skeleton from "../ui/Skeleton";
// Phase 4e — IT/EN parity for the picker's "Who are you?" copy.
import { useLang } from "../../lib/lang";
// Hotfix (2026-05-16) — shared validated worker URL resolver. A stale
// localStorage value pointing at the Pages domain was bricking the
// picker with "listUsers failed: 404 Not found". The util validates +
// self-heals invalid stored values.
import { readWorkerUrl } from "../../lib/worker-url";

/* composed/UserPicker.tsx — "Who are you?" modal (Phase 5.4).

   Shown ONCE between the password gate and the dashboard, the first
   time a user visits or after a sign-out. Lists every active User
   from /api/users; clicking a row stores their id in localStorage
   and lets the dashboard render with the viewer context populated.

   UX notes
     - No search bar: the team is ~5 people. If/when it grows past 20
       add a filter input.
     - Inactive users (active=false) are hidden — they can't be the
       active viewer.
     - The modal is non-dismissible (no X, Escape, or backdrop close):
       you cannot use the platform without a viewer. The legacy edit
       password flow remains for admin-only flows once Phase 5.5 lands.

   Soft-identity reminder: clicking a name does NOT verify you ARE
   that user. The picker improves UX, not security. See §1.6.
*/

const { useState, useEffect, useMemo } = React;

// readWorkerUrl moved to src/lib/worker-url.ts — see import above.

export default function UserPicker() {
  const { setViewer } = useViewer();
  const lang = useLang();
  const [users, setUsers] = useState(null); // null = loading; [] = loaded-empty; [...] = loaded
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(null); // id of the user currently being picked

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pw = (() => { try { return localStorage.getItem("forecast_view_pw"); } catch { return null; } })();
        if (!pw) throw new Error("View password not set — return to the password gate.");
        const list = await listUsers({ workerUrl: readWorkerUrl(), viewPassword: pw });
        if (!cancelled) {
          // Show active users only. Sort: EXEC → MID → AE, alphabetical within.
          const levelPriority = { EXEC: 0, MID: 1, AE: 2 };
          const sorted = list
            .filter(u => u.active)
            .sort((a, b) => {
              const pa = levelPriority[a.level] ?? 99;
              const pb = levelPriority[b.level] ?? 99;
              if (pa !== pb) return pa - pb;
              return (a.name || "").localeCompare(b.name || "");
            });
          setUsers(sorted);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pick = async (u) => {
    setPicking(u.id);
    // tiny delay so the visual feedback shows even on fast networks
    await new Promise(r => setTimeout(r, 80));
    setViewer(u);
    // The viewer state change triggers ForecastItaly's re-render which
    // unmounts this picker. No need to call setPicking(null).
  };

  return (
    <div className="v2-picker-overlay" role="dialog" aria-labelledby="picker-title" aria-modal="true">
      <Card className="v2-picker-card" padding="comfortable" elevation="raised">
        <h2 id="picker-title" className="v2-picker__title">
          {lang === "it" ? "Chi sei?" : "Who are you?"}
        </h2>
        <p className="v2-picker__hint">
          {lang === "it"
            ? "Scegli il tuo nome per continuare. Lo ricorderemo su questo browser."
            : "Pick your name to continue. We'll remember on this browser."}
        </p>

        {/* Phase 3e — section-level states use primitives. */}
        {error && (
          <ErrorState
            lang={lang}
            title={lang === "it" ? "Impossibile caricare gli utenti" : "Couldn't load users"}
            message={error}
          />
        )}

        {users === null && !error && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            <Skeleton variant="block" height={56} />
            <Skeleton variant="block" height={56} />
            <Skeleton variant="block" height={56} />
          </div>
        )}

        {users && users.length === 0 && !error && (
          <EmptyState
            title={lang === "it" ? "Nessun utente trovato" : "No users found"}
            message={lang === "it"
              ? <>Hai eseguito <code>npm run seed:users</code>?</>
              : <>Have you run <code>npm run seed:users</code>?</>}
          />
        )}

        {users && users.length > 0 && (
          <ul className="v2-picker__list">
            {users.map(u => (
              <li key={u.id}>
                <Button
                  variant={picking === u.id ? "outline" : "secondary"}
                  size="lg"
                  onClick={() => pick(u)}
                  disabled={!!picking}
                  aria-pressed={picking === u.id}
                  className="v2-picker__item"
                  style={{
                    width: "100%",
                    justifyContent: "space-between",
                    opacity: picking && picking !== u.id ? 0.5 : 1,
                  }}
                >
                  <span className="v2-picker__item-main">
                    <span className="v2-picker__item-name">{u.name}</span>
                    <span className="v2-picker__item-email">{u.email}</span>
                  </span>
                  <span className="v2-picker__item-label">{u.label}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
